import { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  type BookedFlightItineraryWithDepartureTime,
  CfarContractStatus,
  PortalItineraryStatus,
  ScheduleChangeSeverity,
  Status,
  TravelItineraryEnum,
} from "@b2bportal/air-booking-api";
import { MultiTicketType } from "@b2bportal/air-shopping-api";
import { FlightPostBookingSelectors } from "@b2bportal/core-flights";
import type { TripsAction } from "@b2bportal/core-trips/overridable";
import { useI18nContext } from "@hopper-b2b/i18n";
import { decodeCityName } from "@overrides/utilities";
import { DisruptionContractStatus } from "@tenant/disruption";
import { useFlightItineraryCard as useCoreFlightItineraryCard } from "@tenant/flights";
import type { TripLeg } from "@/overrides/trips/TripsCard/TripsCard";
import { parseSegmentLocalDate } from "@/utilities/flights/segmentDateTime";
import { formatDateWithWeekday } from "@/utilities/formatDate";

interface FlightItineraryCardProps {
  itinerary: BookedFlightItineraryWithDepartureTime;
  selectItinerary?: () => void;
  actions?: TripsAction[];
}

export const useFlightItineraryCard = (props: FlightItineraryCardProps) => {
  const coreResult = useCoreFlightItineraryCard(props);
  const airports = useSelector(FlightPostBookingSelectors.getAirports);
  const airlines = useSelector(FlightPostBookingSelectors.getAirlines);
  const { formatDateTime, t } = useI18nContext();
  const { itinerary } = props;

  const travelItinerary = itinerary.bookedItinerary?.travelItinerary;
  const isHackerFare =
    itinerary.bookedItinerary?.multiTicketType === MultiTicketType.hackerFare;
  const isMulticity =
    travelItinerary?.TravelItinerary ===
      TravelItineraryEnum.MultiTravelItinerary && !isHackerFare;

  const multicityLegs: TripLeg[] | undefined = useMemo(() => {
    if (!isMulticity) return undefined;

    const slices = itinerary.bookedItinerary?.travelItinerary?.slices ?? [];
    return slices
      .map((slice) => {
        const segments = slice.segments;
        if (!segments || segments.length === 0) {
          return null;
        }
        const firstSegment = segments[0];
        const lastSegment = segments[segments.length - 1];
        const origin = firstSegment.origin;
        const destination = lastSegment.destination;
        const airline = firstSegment.marketingAirline;

        const originCity = decodeCityName(
          airports?.[origin.locationCode]?.cityName,
          origin.locationCode
        );
        const destinationCity = decodeCityName(
          airports?.[destination.locationCode]?.cityName,
          destination.locationCode
        );

        const airlineName =
          airlines?.[airline.code]?.displayName ?? airline.code;

        const departureDate = formatDateWithWeekday(
          parseSegmentLocalDate(
            firstSegment.updatedDeparture ?? firstSegment.scheduledDeparture
          ),
          formatDateTime
        );

        const arrivalDate = formatDateWithWeekday(
          parseSegmentLocalDate(
            lastSegment.updatedArrival ?? lastSegment.scheduledArrival
          ),
          formatDateTime
        );

        return {
          title: t("core-flights.itinerary.card.itinerary", {
            origin: `${originCity} (${origin.locationCode})`,
            destination: `${destinationCity} (${destination.locationCode})`,
            // City names are from trusted backend API and have been HTML-decoded
            interpolation: { escapeValue: false },
          }),
          description: airlineName,
          date: `${departureDate} - ${arrivalDate}`,
        };
      })
      .filter((leg): leg is TripLeg => leg !== null);
  }, [
    isMulticity,
    itinerary.bookedItinerary?.travelItinerary?.slices,
    airports,
    airlines,
    formatDateTime,
    t,
  ]);

  const firstSegment =
    itinerary.bookedItinerary?.travelItinerary?.slices?.[0]?.segments?.[0];
  const scheduleChange = itinerary.bookedItinerary?.scheduleChange;
  const hasNotification =
    itinerary.status === Status.Canceled && !!itinerary.travelCredit;
  const hasDisruption =
    itinerary.ancillaries.disruptionProtection?.status
      ?.DisruptionContractStatus ===
      DisruptionContractStatus.ANY_EXERCISE_ELIGIBLE ||
    itinerary.ancillaries.disruptionProtection?.status
      ?.DisruptionContractStatus ===
      DisruptionContractStatus.REBOOK_ONLY_ELIGIBLE;
  const hasScheduleChangeBanner =
    scheduleChange != null &&
    scheduleChange.severity !== ScheduleChangeSeverity.Unknown;
  const slices = itinerary.bookedItinerary?.travelItinerary?.slices ?? [];
  const hasMultipleAirlines =
    isMulticity &&
    new Set(
      slices.flatMap((slice) =>
        slice.segments.map((segment) => segment.marketingAirline.code)
      )
    ).size > 1;

  const context = {
    confirmationCode:
      itinerary.bookedItinerary?.travelItinerary?.locators?.b2b ?? "",
    flightStatus:
      itinerary.cfar?.status === CfarContractStatus.Exercised
        ? PortalItineraryStatus.Canceled
        : itinerary.status,
    firstSegment,
    airlineCode: firstSegment?.marketingAirline?.code ?? "",
    scheduleChangeSeverity: scheduleChange?.severity,
    hasNotification,
    scheduleChange,
    hasScheduleChangeBanner,
    hasBannerContent:
      hasNotification || hasScheduleChangeBanner || hasDisruption,
    multicityLegs,
    isMulticity,
    hasMultipleAirlines,
  };

  const description = isMulticity
    ? coreResult.text.airline
    : `${coreResult.text.tripCategory} | ${coreResult.text.airline}`;

  const cardTitle = isMulticity ? t("flightMultiCity") : "";

  // Override title with decoded city names for non-multicity flights
  const overriddenTitle = useMemo(() => {
    if (isMulticity) return "";

    const firstSlice = itinerary.bookedItinerary?.travelItinerary?.slices?.[0];
    if (!firstSlice || !firstSlice.segments?.length)
      return coreResult.text.title;

    const firstSegment = firstSlice.segments[0];
    const lastSegment = firstSlice.segments[firstSlice.segments.length - 1];
    const origin = firstSegment.origin;
    const destination = lastSegment.destination;

    const originCity = decodeCityName(
      airports?.[origin.locationCode]?.cityName,
      origin.locationCode
    );
    const destinationCity = decodeCityName(
      airports?.[destination.locationCode]?.cityName,
      destination.locationCode
    );

    return t("core-flights.itinerary.card.itinerary", {
      origin: `${originCity} (${origin.locationCode})`,
      destination: `${destinationCity} (${destination.locationCode})`,
      // City names are from trusted backend API and have been HTML-decoded
      interpolation: { escapeValue: false },
    });
  }, [
    isMulticity,
    itinerary.bookedItinerary?.travelItinerary?.slices,
    airports,
    t,
    coreResult.text.title,
  ]);

  // Override date fields with RBC format
  const overriddenDates = useMemo(() => {
    if (!firstSegment) return {};

    const departureDate = formatDateWithWeekday(
      parseSegmentLocalDate(
        firstSegment.updatedDeparture ?? firstSegment.scheduledDeparture
      ),
      formatDateTime
    );

    const returnFirstSegment =
      slices.length === 2 ? slices[1]?.segments?.[0] : null;
    const returnDate = returnFirstSegment
      ? formatDateWithWeekday(
          parseSegmentLocalDate(
            returnFirstSegment.updatedDeparture ??
              returnFirstSegment.scheduledDeparture
          ),
          formatDateTime
        )
      : "";

    return {
      departureDate,
      returnDate,
      dates: [departureDate, returnDate].filter((date) => date).join(" - "),
    };
  }, [firstSegment, slices, formatDateTime]);

  return {
    ...coreResult,
    text: {
      ...coreResult.text,
      description,
      cardTitle,
      title: overriddenTitle || coreResult.text.title,
      ...overriddenDates,
    },
    context,
  };
};
